import { Dayjs } from 'dayjs';
import { InterventionReportModel } from '../models/interventionReportModel';
import { InterventionReportAdvancedFilters } from '../pages/interventionReports/interventionReports.type';
import { AUTO_GENERATED_CREATED_BY } from '../pages/interventionReports/interventionReportsFilterHelper';

const getTimeMinutes = (date: Date): number => {
  const dateObject: Date = new Date(date);
  return dateObject.getHours() * 60 + dateObject.getMinutes();
};

const dayjsToMinutes = (time: Dayjs): number => {
  return time.hour() * 60 + time.minute();
};

const getFullName = (
  person: { firstName: string; lastName: string } | undefined,
): string => {
  if (!person) return '';
  return `${person.firstName} ${person.lastName}`;
};

// Sign/option values are stored as i18n keys prefixed by the report form that
// wrote them — `createHarmReport.*` (auto-generated from a harm report) or
// `createInterventionReport.*` (manually created). The suffix after the prefix
// is identical for the same option, so normalize to the suffix before comparing
// so a single filter selection matches reports of BOTH origins.
const stripReportKeyPrefix = (key: string): string =>
  key.replace(/^create(Harm|Intervention)Report\./, '');

const matchesTotalInterventions = (
  actual: number,
  selectedValue: string,
): boolean => {
  if (selectedValue === '9+') return actual >= 9;
  return actual === Number.parseInt(selectedValue, 10);
};

const matchesTimeCreatedFrom = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.timeCreatedFrom) return true;
  if (!report.createdAt) return false;
  const reportMinutes: number = getTimeMinutes(report.createdAt);
  const filterMinutes: number = dayjsToMinutes(filters.timeCreatedFrom);
  return reportMinutes >= filterMinutes;
};

const matchesTimeCreatedUntil = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.timeCreatedUntil) return true;
  if (!report.createdAt) return false;
  const reportMinutes: number = getTimeMinutes(report.createdAt);
  const filterMinutes: number = dayjsToMinutes(filters.timeCreatedUntil);
  return reportMinutes <= filterMinutes;
};

const matchesPlayerNo = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.playerNo) return true;
  return report.playerProfile?.id === filters.playerNo;
};

const matchesGender = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.gender) return true;
  return report.playerDetails?.gender === filters.gender;
};

const matchesAge = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.age) return true;
  return report.playerDetails?.age === filters.age;
};

const matchesEthnicity = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.ethnicity) return true;
  return report.playerDetails?.ethnicity === filters.ethnicity;
};

const matchesInterventionStatus = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.interventionStatus || filters.interventionStatus.length === 0)
    return true;
  if (!report.interventionStatus) return false;
  return filters.interventionStatus.includes(report.interventionStatus);
};

const matchesHarmInstances = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.harmInstances) return true;
  if (report.harmInstances === undefined) return false;
  if (filters.harmInstances === '10+') return report.harmInstances >= 10;
  return report.harmInstances === Number.parseInt(filters.harmInstances, 10);
};

const matchesHarmObserved = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.harmObserved || filters.harmObserved.length === 0) return true;
  const signs = report.signsOfGamblingHarm;
  const allSigns: Set<string> = new Set(
    [
      ...(signs?.lengthOfPlay ?? []),
      ...(signs?.money ?? []),
      ...(signs?.behaviour ?? []),
      ...(signs?.other ?? []),
    ].map(stripReportKeyPrefix),
  );
  return filters.harmObserved.some((selected: string): boolean =>
    allSigns.has(stripReportKeyPrefix(selected)),
  );
};

const matchesActionTaken = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.actionTaken || filters.actionTaken.length === 0) return true;
  const actions: Set<string> = new Set(report.interventionAction);
  return filters.actionTaken.some((selected: string): boolean =>
    actions.has(selected),
  );
};

const matchesTotalInterventionsOffered = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.totalInterventionsOffered) return true;
  const actual: number =
    report.playerDetails?.gambling?.interventionsOffered ?? 0;
  return matchesTotalInterventions(actual, filters.totalInterventionsOffered);
};

const matchesTotalInterventionsRequested = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.totalInterventionsRequested) return true;
  const actual: number =
    report.playerDetails?.gambling?.interventionsRequested ?? 0;
  return matchesTotalInterventions(actual, filters.totalInterventionsRequested);
};

const matchesCreatedBy = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.createdBy) return true;
  if (filters.createdBy === AUTO_GENERATED_CREATED_BY) {
    return report.isAutoGenerated;
  }
  return getFullName(report.conductedBy) === filters.createdBy;
};

const matchesReviewedBy = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.reviewedBy) return true;
  return getFullName(report.reviewedBy) === filters.reviewedBy;
};

const matchesDateReviewedFrom = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.dateReviewedFrom) return true;
  if (!report.reviewedBy?.signedDate) return false;
  const reviewedDate: Date = new Date(report.reviewedBy.signedDate);
  const fromDate: Date = new Date(filters.dateReviewedFrom);
  fromDate.setHours(0, 0, 0, 0);
  return reviewedDate >= fromDate;
};

const matchesDateReviewedTo = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.dateReviewedTo) return true;
  if (!report.reviewedBy?.signedDate) return false;
  const reviewedDate: Date = new Date(report.reviewedBy.signedDate);
  const toDate: Date = new Date(filters.dateReviewedTo);
  toDate.setHours(23, 59, 59, 999);
  return reviewedDate <= toDate;
};

const matchesReviewStatus = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.reviewStatus || filters.reviewStatus.length === 0) return true;
  return filters.reviewStatus.includes(report.reviewStatus);
};

const matchesCompliance = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.compliance) return true;
  if (report.staffComplied === undefined) return false;
  const expectedComplied: boolean = filters.compliance === 'Compliant';
  return report.staffComplied === expectedComplied;
};

const matchesFollowUpActions = (
  report: InterventionReportModel,
  filters: InterventionReportAdvancedFilters,
): boolean => {
  if (!filters.followUpActions || filters.followUpActions.length === 0)
    return true;
  const actions: Set<string> = new Set(report.followUpAction ?? []);
  return filters.followUpActions.some((selected: string): boolean =>
    actions.has(selected),
  );
};

const FilterPredicates = [
  matchesTimeCreatedFrom,
  matchesTimeCreatedUntil,
  matchesPlayerNo,
  matchesGender,
  matchesAge,
  matchesEthnicity,
  matchesInterventionStatus,
  matchesHarmInstances,
  matchesHarmObserved,
  matchesActionTaken,
  matchesTotalInterventionsOffered,
  matchesTotalInterventionsRequested,
  matchesCreatedBy,
  matchesReviewedBy,
  matchesDateReviewedFrom,
  matchesDateReviewedTo,
  matchesReviewStatus,
  matchesCompliance,
  matchesFollowUpActions,
];

export const filterInterventionReports = (
  reports: InterventionReportModel[],
  filters: InterventionReportAdvancedFilters,
): InterventionReportModel[] => {
  return reports.filter((report: InterventionReportModel): boolean =>
    FilterPredicates.every((predicate): boolean => predicate(report, filters)),
  );
};
